/** @jsxImportSource @emotion/react */
import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import algoliasearch from 'algoliasearch/lite';
import { Heading2, /* Text */ } from '@algolia/ui-library';
import { Configure, Index, InstantSearch } from 'react-instantsearch-dom';
import { X } from 'react-feather';

import TextLink from './components/text-link';
import Link from './components/link';

import styles from './css/pages/search.css';

import * as externalRoutes from './constants/external-routes';
import * as routes from './constants/routes';

import EmptyLayout from './layouts/empty-layout';

import {
  createURL,
  getUrlParameter,
  isBrowser,
  searchStateToUrl,
  urlToSearchState,
} from './helpers';

import Labels from './components/pages/search/labels';
import SearchBox from './components/pages/search/search-box';
import StateResults from './components/pages/search/state-results';
import QuerySuggestions from './components/pages/search/query-suggestions';
import CustomHits, {
  /* CustomBlogInfiniteHits,
  CustomBlogHits,
  CustomDocsInfiniteHits,
  CustomDocsHits,
  CustomLibraryInfiniteHits,
  CustomLibraryHits, */
  CustomIntegrationInfiniteHits,
  CustomIntegrationHits,
  CustomResourcesInfiniteHits,
  CustomResourcesHits,
  CustomWebsiteInfiniteHits,
  CustomWebsiteHits,
} from './components/pages/search/custom-hits';
import { SearchState } from 'react-instantsearch-core';

import config from './config'

const previousPage = routes.HOME;

const siteIndex = config.REACT_APP_ALGOLIA_SITE_INDEX;
const IntegrationIndex = config.REACT_APP_ALGOLIA_Integration_INDEX;
const resourcesIndex = config.REACT_APP_ALGOLIA_RESOURCES_INDEX;
//const eventsIndex = config.REACT_APP_ALGOLIA_EVENTS_INDEX;
// const coursesIndex = config.REACT_APP_ALGOLIA_COURSES_INDEX;
const pressIndex = config.REACT_APP_ALGOLIA_PRESS_INDEX;
const blogsIndex = config.REACT_APP_ALGOLIA_BLOGS_INDEX;
//console.log("config.REACT_APP_ALGOLIA_APP_ID", config.REACT_APP_ALGOLIA_APP_ID)
//console.log("config.REACT_APP_ALGOLIA_SITE_INDEX", config.REACT_APP_ALGOLIA_SITE_INDEX)

const searchClient = algoliasearch(
  config.REACT_APP_ALGOLIA_APP_ID,
  config.REACT_APP_ALGOLIA_API_KEY
);

const SearchPage: FC = () => {
  const searchBox = useRef<HTMLElement>();
  const writeTimer = useRef<ReturnType<typeof setTimeout>>();
  const [searchState, setSearchState] = useState<SearchState>(
    isBrowser ? (urlToSearchState(window.location) as any) : ''
  );
  const [tab, setTab] = useState<string>('');

  useEffect(() => {
    if (!isBrowser) return;
    window.addEventListener('popstate', (e) => {
      setSearchState(urlToSearchState(window.location));
    });
  }, []);

  const onSearchStateChange = (updatedSearchState: SearchState) => {
    if (writeTimer.current) clearTimeout(writeTimer.current);

    if (isBrowser) {
      writeTimer.current = setTimeout(
        () =>
          window.history.pushState(
            {},
            '',
            searchStateToUrl(window.location, updatedSearchState)
          ),
        400
      );
    }

    setSearchState(updatedSearchState);
  };

  useEffect(() => {
    if (writeTimer.current) clearTimeout(writeTimer.current);
  }, [tab]);

  useEffect(() => {
    if (isBrowser) {
      const existingTab = (getUrlParameter(window.location.search, 'tab') ||
        'all') as string;
      if (existingTab !== tab) {
        setTab(existingTab.toLowerCase());
      }

      searchBox.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <EmptyLayout>
        <main css={styles.root} className="p-20 bgc-white mih-100vh">
          <div className="pb-80 pt-0 maw-1200 m-auto sm:pt-24 sm:pb-24 lg:pt-48 lg:pb-48">
           
            <InstantSearch
              searchClient={searchClient}
              indexName={siteIndex}
              searchState={searchState}
              onSearchStateChange={onSearchStateChange}
              createURL={createURL}
            >
              <Labels tab={tab} setTab={setTab} />
              <SearchBox innerRef={searchBox} />
              <QuerySuggestions />

              <Index indexName={siteIndex}>
                <StateResults name="Website" tab={tab}>
                  <CustomHits
                    infiniteHits={<CustomWebsiteInfiniteHits />}
                    hits={<CustomWebsiteHits />}
                  />
                </StateResults>
                <Configure
                  clickAnalytics
                  hitsPerPage={tab === 'website' ? 20 : 6}
                  snippetEllipsisText="…"
                />
              </Index>
              
              <Index indexName={IntegrationIndex}>
                <StateResults name="Integrations" tab={tab}>
                  <CustomHits
                    infiniteHits={<CustomIntegrationInfiniteHits />}
                    hits={<CustomIntegrationHits />}
                  />
                </StateResults>
                <Configure
                  clickAnalytics
                  hitsPerPage={tab === 'integrations' ? 16 : 4}
                  snippetEllipsisText="…"
                  /* attributesToSnippet={['metaDescription:27']} */
                />
              </Index>
              
              <Index indexName={resourcesIndex}>
                <StateResults
                  name="Resources"
                  viewMore={externalRoutes.RESOURCES}
                  tab={tab}
                >
                  <CustomHits
                    infiniteHits={<CustomResourcesInfiniteHits />}
                    hits={<CustomResourcesHits />}
                  />
                </StateResults>
                <Configure
                  clickAnalytics
                  hitsPerPage={tab === 'resources' ? 20 : 4}
                  snippetEllipsisText="…"
                />
              </Index>
              
              {/* <Index indexName={eventsIndex}>
                <StateResults name="Events" tab={tab}>
                  <CustomHits
                    infiniteHits={<CustomWebsiteInfiniteHits />}
                    hits={<CustomWebsiteHits />}
                  />
                </StateResults>
                <Configure
                  clickAnalytics
                  hitsPerPage={tab === 'events' ? 20 : 6}
                  snippetEllipsisText="…"
                />
              </Index> */}

              {/* <Index indexName={coursesIndex}>
                <StateResults name="Courses" tab={tab}>
                  <CustomHits
                    infiniteHits={<CustomWebsiteInfiniteHits />}
                    hits={<CustomWebsiteHits />}
                  />
                </StateResults>
                <Configure
                  clickAnalytics
                  hitsPerPage={tab === 'courses' ? 20 : 6}
                  snippetEllipsisText="…"
                />
              </Index> */}

              <Index indexName={pressIndex}> 
                <StateResults name="Press Releases" tab={tab}>
                  <CustomHits
                    infiniteHits={<CustomWebsiteInfiniteHits />}
                    hits={<CustomWebsiteHits />}
                  />
                </StateResults>
                <Configure
                  clickAnalytics
                  hitsPerPage={tab === 'press releases' ? 20 : 6}
                  snippetEllipsisText="…"
                />
              </Index>



                          
              <Index indexName={blogsIndex}>
                <StateResults
                  name="Blogs"
                  tab={tab}
                >
                  <CustomHits
                    infiniteHits={<CustomResourcesInfiniteHits />}
                    hits={<CustomResourcesHits />}
                  />
                </StateResults>
                <Configure
                  clickAnalytics
                  hitsPerPage={tab === 'blogs' ? 20 : 4}
                  snippetEllipsisText="…"
                />
              </Index>


             
            </InstantSearch>
          </div>
        </main>
        <div
          css={styles.closeButtonContainer}
          className="pb-16 pos-fixed bot-0 left-0 right-0 h-100 d-flex ai-end jc-center sm:d-none"
        >
          <TextLink
            tag={Link}
            className="bxs-default d-flex w-50 h-50 bgc-grey-700 bdr-max"
            to={previousPage}
            aria-label="Close search"
          >
            <X className="m-auto color-white" aria-hidden />
          </TextLink>
        </div>
      </EmptyLayout>
    </>
  );
};

export default SearchPage;
